import { useEffect, useRef, useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { useDebounceValue } from 'usehooks-ts';
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table';
import type { OrderItem } from '@/apis/orders';
import { useVerifyItems } from '@/hooks/useWarehouse';
import { VENDOR_STATUS_VALUES, getVendorStatusStyle } from '../warehouse-shared';

function VendorStatusSelect({
  value,
  onChange,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  const style = getVendorStatusStyle(value);
  return (
    <select
      value={VENDOR_STATUS_VALUES.includes(value as (typeof VENDOR_STATUS_VALUES)[number]) ? value : 'Pending'}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      className={`cursor-pointer rounded-full border-0 px-2.5 py-1 text-xs font-semibold outline-none disabled:cursor-not-allowed disabled:opacity-60 ${style.bg}`}
    >
      {VENDOR_STATUS_VALUES.map((v) => (
        <option key={v} value={v}>
          {v}
        </option>
      ))}
    </select>
  );
}

const COLUMNS = ['Part Number', 'Qty', 'Name', 'Brand', 'Condition', 'Status', 'Verified', 'Missed Qty'];

interface Props {
  orderId: string;
  items: OrderItem[];
  canAct?: boolean;
}

/** Debounces commits so typing a number doesn't fire a mutation (and re-render cascade) per keystroke. */
function MissedQtyInput({
  item,
  onCommit,
  disabled,
}: {
  item: OrderItem;
  onCommit: (qty: number) => void;
  disabled?: boolean;
}) {
  const [value, setValue] = useState(item.missedItemsQty ?? 0);
  const [debounced] = useDebounceValue(value, 500);
  const isFirstRun = useRef(true);

  useEffect(() => {
    setValue(item.missedItemsQty ?? 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item.id, item.missedItemsQty]);

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    if (debounced !== (item.missedItemsQty ?? 0)) onCommit(debounced);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounced]);

  return (
    <input
      type="number"
      min={0}
      max={item.quantity}
      value={value}
      disabled={disabled}
      onChange={(e) => setValue(Math.max(0, Number(e.target.value)))}
      className="h-8 w-16 rounded border border-smoke px-2 text-sm disabled:cursor-not-allowed disabled:opacity-60"
    />
  );
}

export function PackingListSection({ orderId, items, canAct = true }: Props) {
  const verifyItems = useVerifyItems(orderId);
  const missedCount = items.filter((i) => (i.missedItemsQty ?? 0) > 0).length;

  return (
    <section>
      <h2 className="mb-3 text-lg font-semibold text-black">Packing List</h2>
      <div className="overflow-hidden rounded-2xl border border-smoke">
        <Table>
          <TableHeader>
            <TableRow>
              {COLUMNS.map((col) => (
                <TableHead key={col} className="text-xs font-semibold uppercase text-lightGray">
                  {col}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMNS.length} className="py-6 text-center text-sm text-lightGray">
                  No packing list items
                </TableCell>
              </TableRow>
            ) : (
              items.map((item) => (
                <TableRow key={item.id}>
                  <TableCell className="font-semibold text-black">{item.productSnapshot?.partNumber || item.sku}</TableCell>
                  <TableCell>{item.quantity}</TableCell>
                  <TableCell>{item.productSnapshot?.name || item.productName}</TableCell>
                  <TableCell>{item.productSnapshot?.brandName || '-'}</TableCell>
                  <TableCell className="text-lightGray">{item.productSnapshot?.condition || '-'}</TableCell>
                  <TableCell>
                    <VendorStatusSelect
                      value={item.vendorStatus || 'Pending'}
                      onChange={(vendorStatus) => verifyItems.mutate({ items: [{ id: item.id, vendorStatus }] })}
                      disabled={!canAct}
                    />
                  </TableCell>
                  <TableCell>
                    <input
                      type="checkbox"
                      checked={!!item.isVerified}
                      onChange={(e) =>
                        verifyItems.mutate({ items: [{ id: item.id, isVerified: e.target.checked }] })
                      }
                      disabled={!canAct}
                      className="h-4 w-4 rounded border-smoke accent-primary-s500 disabled:cursor-not-allowed disabled:opacity-60"
                    />
                  </TableCell>
                  <TableCell>
                    <MissedQtyInput
                      item={item}
                      onCommit={(missedItemsQty) => verifyItems.mutate({ items: [{ id: item.id, missedItemsQty }] })}
                      disabled={!canAct}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      <div className="mt-3 flex items-center justify-between">
        <p className="text-sm font-normal text-lightGray">
          Showing {items.length} of {items.length} items found
        </p>
        <span
          className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-medium ${
            missedCount === 0 ? 'bg-success text-white' : 'bg-warning text-white'
          }`}
        >
          <CheckCircle2 size={14} />
          {missedCount === 0 ? 'All Items Found' : `${missedCount} Item(s) Missed`}
        </span>
      </div>
    </section>
  );
}
